import { Link } from 'react-router-dom';
import { usePageTitle } from '../../hooks/usePageTitle';
import { CITIES } from '../../data/broward-cities';
import { SERVICES } from '../../data/services';

const SITE_ORIGIN = 'https://floridaaimarketingexperts.com';

export default function ServiceAreasPage() {
  usePageTitle({
    title: 'Service Areas | Florida AI Marketing Experts | Broward County AI Agency',
    description:
      'AI-first marketing retainers for Broward County businesses. Voice AI, generative engine optimization, SEO automation, ChatGPT ads, and integrated growth across 15 Broward cities.',
    keywords:
      'Broward County AI marketing agency, Florida AI marketing, generative engine optimization Broward, ChatGPT ads Florida, voice AI agency Broward',
    canonical: `${SITE_ORIGIN}/service-areas`,
  });

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      <nav className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="text-navy-900 font-bold text-lg">
            Florida AI Marketing Experts
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/calculators" className="hidden md:inline text-sm text-navy-700 hover:text-teal-600">
              Free Tools
            </Link>
            <Link
              to="/audit"
              className="bg-teal-500 text-navy-900 px-4 py-2 rounded-full text-sm font-semibold hover:bg-teal-600 transition"
            >
              Free Assessment
            </Link>
          </div>
        </div>
      </nav>

      <section className="relative overflow-hidden bg-white grid-bg pt-36 pb-16">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 30% 0%, rgba(13,148,136,0.14), transparent 58%)' }}
        ></div>
        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <div className="eyebrow mb-5">Service Areas</div>
          <h1 className="h-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-[-0.02em] mb-4">
            Service Areas — {CITIES.length} Broward cities, {SERVICES.length} AI services
          </h1>
          <p className="text-lg text-slate-600 font-body font-light max-w-3xl">
            This is FAME&apos;s Broward service-area directory. We are an AI-first marketing agency
            running ChatGPT visibility, voice AI, SEO automation, and generative-search content for
            Florida businesses. Pick a service below, then your city, for a local plan with pricing
            and ROI math.
          </p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          {SERVICES.map((service) => (
            <div key={service.slug} className="mb-12">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl md:text-3xl font-display font-bold text-navy-900">
                  {service.name} Agency
                </h2>
                <Link
                  to={service.industry_route}
                  className="text-sm text-teal-700 font-semibold hover:underline"
                >
                  Learn more about {service.name} →
                </Link>
              </div>
              <p className="text-navy-600 mb-6 max-w-3xl">{service.tagline}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {CITIES.map((city) => (
                  <Link
                    key={`${service.slug}-${city.slug}`}
                    to={`/services/${service.slug}/${city.slug}`}
                    className="bg-navy-50 hover:bg-teal-50 rounded-lg p-3 border border-navy-100 hover:border-teal-300 transition"
                  >
                    <div className="text-xs text-navy-500 uppercase tracking-wide">
                      {city.county} Co.
                    </div>
                    <div className="text-navy-900 font-semibold text-sm">{city.name}</div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <footer className="bg-navy-900 text-white/80 py-10">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-sm">
            {CITIES.length * SERVICES.length} city-specific AI marketing plans across {CITIES.length}{' '}
            Broward cities.
          </div>
          <div className="text-sm">
            <a href="tel:+19542964896" className="mr-4 hover:text-white">
              (954) 296-4896
            </a>
            <Link to="/" className="hover:text-white">
              Home
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
